import { appConfig } from "@/config";
import { isNative } from "@/lib/capacitor";

// OneSignal push notifications.
//
// The local notifications in `notifications.ts` are raised by this app while it
// is running, so they die with the webview — which iOS suspends soon after the
// app is backgrounded. These are *server-sent* pushes: the API asks OneSignal
// to deliver on every inbound message and the OS wakes the app to show it, so
// they arrive with the app backgrounded, killed, or the screen off.
//
// Two SDKs behind one API, mirroring notifications.ts: the Cordova plugin in
// the native shells (Capacitor injects the cordova bridge into the remote
// `server.url` page too, so the plugin works even though the site is hosted),
// and the OneSignal Web SDK from their CDN in a plain browser. Everything
// no-ops when VITE_ONESIGNAL_APP_ID is unset, so the app still runs — falling
// back to local notifications — without a OneSignal account.

const WEB_SDK_URL =
  "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";

type NativeOneSignal = typeof import("onesignal-cordova-plugin").default;

// Minimal shape of the v16 Web SDK, which mirrors the native namespaces except
// that `PushSubscription` is capitalized. Only the parts used here.
interface WebOneSignal {
  init(options: Record<string, unknown>): Promise<void>;
  login(externalId: string): Promise<void>;
  logout(): Promise<void>;
  User: {
    PushSubscription: {
      optedIn?: boolean;
      optIn(): Promise<void>;
      optOut(): Promise<void>;
      addEventListener(
        event: "change",
        handler: (change: { current?: { optedIn?: boolean } }) => void,
      ): void;
    };
  };
  Notifications: {
    permission: boolean;
    requestPermission(): Promise<void>;
    addEventListener(
      event: "click",
      handler: (event: { notification?: { additionalData?: unknown } }) => void,
    ): void;
  };
}

type OneSignalWindow = Window & {
  OneSignalDeferred?: ((oneSignal: WebOneSignal) => void)[];
};

let nativeSdk: NativeOneSignal | null = null;
let webSdk: WebOneSignal | null = null;
let initPromise: Promise<void> | null = null;
let subscribed = false;
let onOpen: ((conversationId: string) => void) | null = null;

/** True when a OneSignal app id is configured for this deployment. */
export function pushConfigured(): boolean {
  return appConfig.oneSignalAppId.length > 0;
}

/**
 * True when push is configured AND this device is actually subscribed — the
 * signal `notifications.ts` uses to stop raising a local notification for a
 * message OneSignal is already pushing.
 */
export function pushActive(): boolean {
  return pushConfigured() && subscribed;
}

/** Registers where a tapped push should navigate (same handler as local). */
export function setPushOpenHandler(
  handler: (conversationId: string) => void,
): void {
  onOpen = handler;
}

function conversationIdOf(additionalData: unknown): string | null {
  if (typeof additionalData !== "object" || additionalData === null) {
    return null;
  }
  const id = (additionalData as { conversationId?: unknown }).conversationId;
  return typeof id === "string" ? id : null;
}

// --- Native (Capacitor shells) ---------------------------------------------

async function initNative(externalId: string): Promise<void> {
  const module = await import("onesignal-cordova-plugin");
  // CJS interop: the plugin ships `exports.default`, and what a bundler hands
  // back differs between the dev server and the production build.
  const sdk = (module.default ?? module) as NativeOneSignal;

  sdk.initialize(appConfig.oneSignalAppId);
  sdk.login(externalId);

  sdk.Notifications.addEventListener("click", (event) => {
    const conversationId = conversationIdOf(event.notification.additionalData);
    if (conversationId) onOpen?.(conversationId);
  });

  // App on screen: the inbox already updates live, so drop the banner rather
  // than interrupt the driver with something they can see.
  sdk.Notifications.addEventListener("foregroundWillDisplay", (event) => {
    if (document.visibilityState === "visible") event.preventDefault();
  });

  sdk.User.pushSubscription.addEventListener("change", (change) => {
    subscribed = change.current.optedIn === true;
  });

  nativeSdk = sdk;
  subscribed = await sdk.User.pushSubscription
    .getOptedInAsync()
    .catch(() => false);
}

// --- Web (plain browser) ---------------------------------------------------

function loadWebSdk(): Promise<WebOneSignal | null> {
  return new Promise((resolve) => {
    const target = window as OneSignalWindow;
    target.OneSignalDeferred ??= [];
    // The SDK drains this queue once it boots; queueing before the script is
    // even on the page is the documented usage.
    target.OneSignalDeferred.push((oneSignal) => resolve(oneSignal));

    if (document.querySelector(`script[src="${WEB_SDK_URL}"]`)) return;
    const script = document.createElement("script");
    script.src = WEB_SDK_URL;
    script.defer = true;
    script.addEventListener("error", () => resolve(null));
    document.head.append(script);
  });
}

async function initWeb(externalId: string): Promise<void> {
  const sdk = await loadWebSdk();
  if (!sdk) return;

  // Worker paths are relative to the site root; `assetsBasePath` is "" unless
  // the app is deployed under a sub-path, where the worker ships alongside it.
  const base = appConfig.assetsBasePath;
  await sdk.init({
    appId: appConfig.oneSignalAppId,
    serviceWorkerPath: `${base}/OneSignalSDKWorker.js`.replace(/^\//, ""),
    serviceWorkerParam: { scope: `${base}/` },
    allowLocalhostAsSecureOrigin: true,
  });
  await sdk.login(externalId);

  sdk.Notifications.addEventListener("click", (event) => {
    const conversationId = conversationIdOf(event.notification?.additionalData);
    if (conversationId) onOpen?.(conversationId);
  });

  sdk.User.PushSubscription.addEventListener("change", (change) => {
    subscribed = change.current?.optedIn === true;
  });

  webSdk = sdk;
  subscribed = sdk.User.PushSubscription.optedIn === true;
}

// --- Lifecycle -------------------------------------------------------------

/**
 * Boots the SDK and binds this device to the signed-in driver: the API targets
 * pushes by `external_id`, so the driver id must be the OneSignal external id.
 * Idempotent — safe to call on every mount of the authenticated shell.
 */
export function initPush(externalId: string): Promise<void> {
  if (!pushConfigured() || !externalId) return Promise.resolve();
  initPromise ??= (
    isNative() ? initNative(externalId) : initWeb(externalId)
  ).catch(() => {
    // Plugin missing from the shell, offline, or no platform configured in
    // the dashboard — local notifications still cover the foreground case.
    initPromise = null;
  });
  return initPromise;
}

/** Detaches this device from the driver on logout so pushes stop following it. */
export async function pushLogout(): Promise<void> {
  try {
    nativeSdk?.logout();
    await webSdk?.logout();
  } catch {
    // Nothing to detach.
  }
  subscribed = false;
}

/**
 * Prompts for the OS notification permission through OneSignal (which also
 * registers the push subscription) and opts the device in. Must be called from
 * a user gesture. Returns whether the device ended up subscribed.
 */
export async function requestPushPermission(): Promise<boolean> {
  await initPromise;
  try {
    if (nativeSdk) {
      // `true`: a driver who denied earlier is sent to the OS settings page.
      const granted = await nativeSdk.Notifications.requestPermission(true);
      if (!granted) return false;
      nativeSdk.User.pushSubscription.optIn();
      subscribed = true;
      return true;
    }
    if (webSdk) {
      await webSdk.Notifications.requestPermission();
      if (!webSdk.Notifications.permission) return false;
      await webSdk.User.PushSubscription.optIn();
      subscribed = true;
      return true;
    }
  } catch {
    // Prompt refused or unavailable.
  }
  return false;
}

/** Mirrors the in-app notifications switch onto the OneSignal subscription. */
export async function setPushSubscribed(enabled: boolean): Promise<void> {
  await initPromise;
  try {
    if (nativeSdk) {
      if (enabled) nativeSdk.User.pushSubscription.optIn();
      else nativeSdk.User.pushSubscription.optOut();
      subscribed = enabled;
      return;
    }
    if (webSdk) {
      if (enabled) await webSdk.User.PushSubscription.optIn();
      else await webSdk.User.PushSubscription.optOut();
      subscribed = enabled;
    }
  } catch {
    // Subscription change refused — the in-app preference still holds.
  }
}
